import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useEditEquipmentMutation, useEquipmentDetailsQuery } from 'api';
import { EquipmentFormValues } from 'types';

import { formValuesAdapter } from '../../RegisterEquipmentForm/adapter';
import { defaultValuesAdapter } from '../adapter';

export function useEditEquipmentForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { equipmentId, employeeId } = useParams();

  const {
    data,
    isLoading: isDetailsLoading,
    isSuccess: isDetailsLoaded,
  } = useEquipmentDetailsQuery({
    userId: employeeId ?? '',
    equipmentId: equipmentId ?? '',
  });

  const {
    editEquipmentMutation,
    isLoading: isEditEquipmentLoading,
    isSuccess,
  } = useEditEquipmentMutation({
    invalidateQueries: ['equipment'],
    successText: t('toasts.equipments.success.edit'),
  });

  const handleSubmit = useCallback(
    async (data: EquipmentFormValues, attachmentIds: string[]) => {
      await editEquipmentMutation({
        data: {
          ...formValuesAdapter(data),
          ...(!!attachmentIds.length && { file_ids: attachmentIds }),
        },
        userId: employeeId ?? '',
        equipmentId: equipmentId ?? '',
      });
    },
    [editEquipmentMutation, employeeId, equipmentId],
  );

  useEffect(() => {
    if (isSuccess) navigate(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const defaultValues = (isDefaultEditable: boolean) =>
    defaultValuesAdapter({ data, isDefaultEditable });

  return {
    isDetailsLoading,
    isDetailsLoaded,
    isEditEquipmentLoading,
    defaultValues,
    handleSubmit,
  };
}
